import React, { useEffect, useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ProfilePage.module.css";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import NavigationBar from "../../components/common/NavigationBar";
import DateInputWrapper from "../../components/DateWheelPicker/DateInputWrapper";
import TimeInputWrapper from "../../components/TimeWheelPicker/TimeInputWrapper";
import Popup from "../../components/common/Popup";
import StepError from "../../components/common/StepError";
import Preloader from "../../components/common/Preloader";
import { useProfileStore, Profile } from "../../store/profileStore";
import { updateProfile, fetchProfiles, fetchNatalChart } from "../../services/api/profile";
import { useCityAutoComplete } from "../../hooks/useCityAutoComplete";
import { useNatalChartStore } from "../../store/natalChartStore";
import { useForecastStore } from "../../store/forecastStore";
import { getUserFriendlyError } from "../../utils/errorMessages";
import { useFormDataStore } from "../../store/formDataStore";

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 50;
const NAME_REGEX = /^[A-Za-zА-Яа-яЁё\s-]+$/;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const profile = useProfileStore((state) => state.profile);
  const setProfile = useProfileStore((state) => state.setProfile);
  const setNatalChart = useNatalChartStore((state) => state.setNatalChart);
  const clearForecasts = useForecastStore((state) => state.clearForecasts);
  const { formData } = useFormDataStore();

  const { citySuggestions, showSuggestions, handleInputChange, handleCitySelect } =
    useCityAutoComplete();

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [place, setPlace] = useState("");
  const [birthDate, setBirthDate] = useState<Date>(new Date());
  const [birthTime, setBirthTime] = useState("00:00");
  const [birthTimeUnknown, setBirthTimeUnknown] = useState(false);
  const [sex, setSex] = useState<"male" | "female">("female");

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [updates, setUpdates] = useState<Partial<Profile> & { dob?: string }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isEditable = profile?.edit_count !== 1;

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setPlace(profile.city);
      setSex(profile.sex === "male" ? "male" : "female");

      const [year, month, day] = profile.dob.split("T")[0].split("-");
      setBirthDate(new Date(+year, +month - 1, +day, 12, 0, 0));

      const timeStr = profile.dob.split("T")[1]?.slice(0, 5) || "00:00";
      setBirthTime(timeStr);
      setBirthTimeUnknown(timeStr === "00:00");
    }
  }, [profile]);

  const validateName = (value: string): string | null => {
    if (!value.trim()) return "Обязательное поле";
    if (!NAME_REGEX.test(value))
      return "Допустимы только буквы латиницы и кириллицы, пробел и тире";
    if (value.length < MIN_NAME_LENGTH || value.length > MAX_NAME_LENGTH) {
      return `Имя должно быть от ${MIN_NAME_LENGTH} до ${MAX_NAME_LENGTH} символов`;
    }
    return null;
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(validateName(value));
  };

  const prepareUpdates = () => {
    if (!profile) return {};

    const changes: Partial<Profile> & { dob?: string } = {};

    if (name !== profile.name) changes.name = name;
    if (place !== profile.city) {
      changes.city = place;
      changes.timezone = formData.timezone || "";
      changes.country_code = formData.countryCode || "";
      changes.latitude = formData.latitude || 0;
      changes.longitude = formData.longitude || 0;
    }
    if (sex !== profile.sex) changes.sex = sex;

    const y = birthDate.getFullYear();
    const m = String(birthDate.getMonth() + 1).padStart(2, "0");
    const d = String(birthDate.getDate()).padStart(2, "0");
    const t = birthTimeUnknown ? "00:00" : birthTime;
    const newDob = `${y}-${m}-${d}T${t}:00.000Z`;

    if (newDob !== profile.dob) {
      changes.dob = newDob;
    }

    return changes;
  };

  const handleSaveClick = () => {
    const nameValidationError = validateName(name);
    setNameError(nameValidationError);

    if (nameValidationError) return;

    const changes = prepareUpdates();
    if (Object.keys(changes).length === 0) {
      console.warn("⚠️ Нет изменений для сохранения");
      setErrorMessage("Нет изменений для сохранения");
      return;
    }

    setUpdates(changes);
    setShowConfirmPopup(true);
  };

  const handleConfirm = async () => {
    if (!profile || Object.keys(updates).length === 0) return;

    setErrorMessage(null);
    setLoading(true);
    setShowConfirmPopup(false);

    try {
      await updateProfile(profile.id, updates);

      for (let i = 0; i < 10; i++) {
        const profiles = await fetchProfiles();
        const main = profiles.find((p: Profile) => p.is_main);

        if (main?.forecast === 1) {
          setProfile(main);
          clearForecasts();

          const chart = await fetchNatalChart();

          if (chart?.natalChart?.summary) {
            setNatalChart(chart.natalChart);
            navigate("/natal-chart");
            return;
          } else {
            throw new Error("Натальная карта не содержит summary");
          }
        }

        await new Promise((r) => setTimeout(r, 2000));
      }

      throw new Error("Прогноз не готов: превышено время ожидания");
    } catch (err) {
      console.error("❌ Ошибка при обновлении профиля или получении карты:", err);
      setErrorMessage(getUserFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Preloader />;
  if (errorMessage) {
    return <StepError message={errorMessage} onRetry={() => setErrorMessage(null)} />;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Профиль</h1>

      <div className={styles.form}>
        <Input
          value={name}
          onChange={handleNameChange}
          placeholder="Введите имя"
          disabled={!isEditable}
          className={nameError ? styles.invalid : undefined}
        />
        {nameError && <div className={styles.errorText}>{nameError}</div>}

        <div style={{ position: "relative" }}>
          <Input
            value={place}
            onChange={(e) => {
              setPlace(e.target.value);
              handleInputChange(e.target.value);
            }}
            placeholder="Место рождения (город, страна)"
            disabled={!isEditable}
          />
          {isEditable && showSuggestions && citySuggestions.length > 0 && (
            <ul className={styles.suggestionList}>
              {citySuggestions.map((city, idx) => (
                <li
                  key={idx}
                  className={styles.suggestionItem}
                  onClick={async () => {
                    await handleCitySelect(city);
                    setPlace(city);
                  }}
                >
                  {city}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DateInputWrapper
          value={birthDate.toLocaleDateString("ru-RU")}
          onChange={(date) => setBirthDate(date)}
          initialDate={birthDate}
          disabled={!isEditable}
        />

        <div className={styles.birthTimeWrapper}>
          <TimeInputWrapper
            value={birthTime}
            onChange={(time) => setBirthTime(time)}
            initial={birthTime}
            disabled={!isEditable}
          />
        </div>

        {isEditable && (
          <div className={styles.radioGroup}>
            <label>
              <input
                type="radio"
                name="sex"
                value="male"
                checked={sex === "male"}
                onChange={() => setSex("male")}
              />
              Мужской
            </label>
            <label>
              <input
                type="radio"
                name="sex"
                value="female"
                checked={sex === "female"}
                onChange={() => setSex("female")}
              />
              Женский
            </label>
          </div>
        )}

        {isEditable ? (
          <Button onClick={handleSaveClick}>Сохранить изменения</Button>
        ) : (
          <div className={styles.supportBlock}>
            <p>Для изменения данных профиля обратитесь в службу поддержки</p>
            <a
              href={process.env.REACT_APP_BOT}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "block", textDecoration: "none" }}
            >
              <Button>Написать в поддержку</Button>
            </a>
          </div>
        )}
      </div>

      {showConfirmPopup && (
        <Popup
          type="confirm"
          message="Профиль можно редактировать только один раз. Вы уверены, что хотите сохранить изменения?"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmPopup(false)}
        />
      )}

      <NavigationBar active="profile" />
    </div>
  );
};

export default ProfilePage;
